import path from "path";
import { promises as fs } from "fs";
import { useRouter } from "next/router";
import Seo from "@/components/common/Seo";
import JsonLd, {
  offerListSchema,
  breadcrumbSchema,
} from "@/components/common/JsonLd";
import VergleichBlock from "@/components/sections/VergleichBlock";
import VergleichInhalt from "@/components/sections/VergleichInhalt";
import FaqSection from "@/components/sections/FaqSection";
import { preisAnalyse } from "@/utils/preisAnalyse";
import { vergleichFaq } from "@/utils/vergleichFaq";
import { loadCatalog } from "@/lib/catalog";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

import Header from "@/components/common/Header";
import Footer from "@/components/common/Footer";
import CookieBanner from "@/components/CookieBanner";

export async function getStaticProps({ locale }) {
  // Die Liste kam bisher ausschließlich per fetch im Browser. Für Google war
  // der Vergleich damit eine leere Seite mit einer Überschrift: 122 Wörter,
  // keine Anbieter, keine Preise. Jetzt steht der Katalog im HTML — der
  // Browser holt ihn trotzdem noch einmal, damit frisch gebuchte Termine
  // sofort verschwinden.
  let initialProducts = { Photobooths: [] };
  try {
    if (locale === "en") {
      const datei = path.join(process.cwd(), "data", "dataEN.json");
      initialProducts = JSON.parse(await fs.readFile(datei, "utf8"));
    } else {
      initialProducts = await loadCatalog();
    }
  } catch (error) {
    console.error("❌ Vergleich: Katalog nicht ladbar:", error);
  }

  const alleBoxen = initialProducts.Photobooths || [];
  const analyse = preisAnalyse(alleBoxen);
  const merkmale = {
    anzahlBoxen: alleBoxen.length,
    anzahlAnbieter: new Set(
      alleBoxen.map((box) => box.PartnerName).filter(Boolean)
    ).size,
    spiegelreflex: alleBoxen.filter((box) =>
      /Spiegelreflex|DSLM|Systemkamera/i.test(box.CamType || "")
    ).length,
    studioblitz: alleBoxen.filter((box) => /Blitz/i.test(box.LightType || ""))
      .length,
  };

  return {
    props: {
      initialProducts,
      analyse,
      merkmale,
      stand: new Date().toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
      ...(await serverSideTranslations(locale, ["common"])),
    },
// Frist von einer Stunde auf einen Tag (Absprache 2026-10-03):
// Jede Neuerzeugung schreibt die Seite in Vercels Zwischenspeicher, und
// abgerechnet wird in Blöcken zu 8 KB. Eine Seite von ~300 KB kostet so
// rund 36 Einheiten — pro Erzeugung. Stündlich erneuert reicht eine
// Handvoll gecrawlter Seiten, um das Monatskontingent von 200.000
// aufzubrauchen; am 2026-10-03 waren 75 % verbraucht, und bei 100 %
// schaltet Vercel das Projekt ab.
//
// Inhaltlich kostet es nichts: Was sich hier ändert, hängt am Tagesstand
// (daySeed, stand) und wechselt ohnehin nur einmal am Tag.
    revalidate: 86400,
  };
}

export default function FotoboxVergleichPage({
  initialProducts,
  analyse,
  merkmale,
  stand,
}) {
  const router = useRouter();
  const { locale } = router;
  const products = initialProducts || { Photobooths: [] };

  const metaTagOgImageUrl = "https://fotoboxguru.de/images/og-fotobox.jpg";
  const metaTagThumbnail = "https://fotoboxguru.de/images/thumb-fotobox.jpg";
  const metaTagTitle =
    "Fotobox Vergleich – Preise, Druck & Ausstattung im Check";
  const metaTagDesc =
    "Fotobox Vergleich: Filtere nach Preis, Fotodruck, Ausstattung und Service und finde schnell die passende Fotobox für dein Event.";
  const h1Title = "Fotobox Vergleich – Preise, Fotodruck & Ausstattung";
  const metaTagKeywords = "Fotobox Vergleich";
  const metaTagOgLocale = "de_DE";
  const metaTagOgUrl = "https://fotoboxguru.de/fotobox-vergleich";

  return (
    <>
      {/* Kanonisch auf die Startseite (Absprache 2026-10-07): Seit der
          Vergleich dort steht, sind beide Seiten dieselbe Liste. Diese Seite
          bleibt erreichbar — die Stadtseiten verlinken ihre gefilterte
          Ansicht (?delivery=regional&postalCode=…), der Finder sein
          Ergebnis —, aber werten soll Google die Startseite. Es kostet, was
          diese Seite in der Suche einbringt: 8 Klicks in 90 Tagen auf
          Position 41,8. */}
      <Seo
        title={metaTagTitle}
        description={metaTagDesc}
        keywords={metaTagKeywords}
        canonicalPath="/"
        image={metaTagOgImageUrl}
      >
        <meta name="thumbnail" content={metaTagThumbnail} />
      </Seo>

      <JsonLd
        id="breadcrumb-vergleich"
        data={breadcrumbSchema([
          { name: "Fotobox mieten", path: "/" },
          { name: "Fotobox-Vergleich", path: "/fotobox-vergleich" },
        ])}
      />
      <JsonLd
        id="vergleich-liste"
        data={offerListSchema(
          (products?.Photobooths || []).slice(0, 30).map((pb) => ({
            name: pb.PhotoboothName,
            partnername: pb.PartnerName,
            image: pb.PhotoboothImage,
            packages: (pb.Packages || []).map((paket) => ({
              priceValue:
                (typeof pb.PhotoboothPrice === "number"
                  ? pb.PhotoboothPrice
                  : 0) +
                (typeof paket.PackagePrice === "number"
                  ? paket.PackagePrice
                  : 0),
            })),
          })),
          {
            name: "Fotobox-Vergleich",
            locale,
            path: "/fotobox-vergleich",
          }
        )}
      />

      <div className="bg-[#F9F9F9]">
        <Header sticky={false} />

        <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:max-w-7xl lg:px-8 lg:py-10">
          <div className="pb-5 space-y-2">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900 lg:text-4xl flex flex-col gap-1">
              {h1Title}
            </h1>
            <p className="max-w-2xl text-[15px] text-slate-600">
              {analyse
                ? `${merkmale?.anzahlBoxen} Fotoboxen von ${merkmale?.anzahlAnbieter} Anbietern mit ${analyse.anzahlPakete} Paketen — nach denselben Kriterien verglichen. Filtere nach Gästezahl, Lieferweg und Druck.`
                : "Filtere nach Gästezahl, Lieferweg und Druck und finde die passende Fotobox."}
            </p>
          </div>

          <VergleichBlock initialProducts={initialProducts} initialVisible={20} />

          {/* Erklärung unter die Liste, nicht davor: Wer vergleichen will,
              soll nicht erst an einem Aufsatz vorbeiscrollen. */}
          <VergleichInhalt
            analyse={analyse}
            merkmale={merkmale}
            stand={stand}
          />

          <div className="pt-12 lg:pt-16">
            <FaqSection
              title="Häufige Fragen zum Fotobox-Vergleich"
              subtitle="Was beim Vergleichen am häufigsten gefragt wird."
              items={vergleichFaq(analyse, merkmale)}
            />
          </div>
        </main>

        <Footer />
      </div>

      <CookieBanner />
    </>
  );
}
